import { useState, useRef } from "react";
import { useAdmin } from "@/hooks/useAdmin";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { WordFadeIn } from "@/components/ui/word-fade-in";
import { Pause, Play } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.15 } }
};

const HeroSection = () => {
  const { isAdmin } = useAdmin();
  const [heroVideoFile, setHeroVideoFile] = useState<string | null>(null);
  const [heroAudioFile, setHeroAudioFile] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setHeroVideoFile(URL.createObjectURL(file));
  };

  const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setHeroAudioFile(url);
      if (audioRef.current) {
        audioRef.current.src = url;
      }
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current || !heroAudioFile) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section aria-label="Hero" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Video background – tap to play/pause, no native controls */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        aria-hidden="true"
        autoPlay
        loop
        muted
        playsInline
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        className="absolute inset-0 w-full h-full object-cover pointer-events-none [&::-webkit-media-controls]:!hidden [&::-webkit-media-controls-enclosure]:!hidden [&::-webkit-media-controls-panel]:!hidden [&::-webkit-media-controls-start-playback-button]:!hidden"
        style={{ WebkitAppearance: 'none' } as React.CSSProperties}
        src={heroVideoFile || "/videos/hero-placeholder.mp4"}
      />
      {/* Small visible video pause/play control – bottom-right, unobtrusive */}
      <button
        className="absolute bottom-24 right-4 z-20 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 text-white/80 hover:text-white hover:bg-black/60 transition-colors"
        aria-label={isVideoPaused ? "Play background video" : "Pause background video"}
        onClick={() => {
          if (videoRef.current) {
            if (videoRef.current.paused) {
              videoRef.current.play();
              setIsVideoPaused(false);
            } else {
              videoRef.current.pause();
              setIsVideoPaused(true);
            }
          }
        }}
      >
        {isVideoPaused ? <Play className="w-4 h-4" aria-hidden="true" /> : <Pause className="w-4 h-4" aria-hidden="true" />}
      </button>

      {/* Fallback gradient while video loads */}
      <div className="absolute inset-0 hero-gradient-bg -z-10" />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-foreground/50" />

      {/* Upload banner for video (admin placeholder) */}
      {isAdmin && !heroVideoFile &&
      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20">
          <button
          onClick={() => videoInputRef.current?.click()}
          className="bg-card/90 backdrop-blur-sm text-foreground text-xs px-4 py-2 rounded-full border border-border shadow-soft hover:bg-card transition-colors max-w-md text-center leading-relaxed">

            🎬 Upload Hero Video — 15–30 sec, no audio, looping. Ideal: couple dancing, someone hearing their song, hands exchanging a gift.
          </button>
          <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleVideoUpload} />

        </div>
      }

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="max-w-3xl mx-auto">

          <WordFadeIn
            words={"The Gift\nThey'll Never Forget."}
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            delay={0.12}
          />

          <motion.p
            variants={fadeInUp}
            className="text-lg text-white/85 mb-8 max-w-2xl mx-auto leading-relaxed font-body md:text-2xl">
            A custom song written from your story<br />or created as a heartfelt gift for someone you love.
          </motion.p>

          {/* Audio preview widget */}
          

















































          {/* CTA buttons */}
          <motion.div
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">

            <Button
              size="xl"
              className="min-h-[48px] font-bold"
              onClick={() => scrollTo("pricing")}>

              Start Your Song
            </Button>
            <Button
              variant="outline"
              size="xl"
              className="min-h-[48px] font-bold border-2 border-white text-white hover:bg-white/10 hover:text-white"
              onClick={() => scrollTo("listen")}>

              Hear a Real Song
            </Button>
          </motion.div>

          {/* Trust line */}
          <motion.p
            variants={fadeInUp}
            className="text-white font-bold font-body text-lg text-center">

            Delivered in time for your big day · 48–72 hour turnaround · Rush delivery available  
          </motion.p>
        </motion.div>
      </div>

      {/* Decorative wave accent at bottom */}
      <div className="absolute -bottom-1 left-0 w-full z-10 pointer-events-none">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-[60px] md:h-[80px] block" preserveAspectRatio="none" aria-hidden="true" role="presentation">
          <path d="M0 20C240 45 480 55 720 40C960 25 1200 5 1440 20V80H0V20Z" fill="hsl(var(--primary))" />
        </svg>
      </div>
    </section>);

};

export default HeroSection;